import "./ResightingAssignments.css";
import { useContext, useEffect, useState } from "react";
import { Alert, Card, CardContent, Snackbar } from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import AppContext from "../../Context/AppContext";
import ResightingRecordDetails from "./ResightingRecordDetails";
import ResightingActivitySection from "./ResightingActivitySection";
import {
  buildRecordDetailsFromApi,
  formatQcStatus,
  getQcAccessStatus,
} from "./recordDetailsUtils";

const buildAuthHeaders = (authToken) => ({
  "authentication-token": authToken,
  "Access-Control-Allow-Origin": "*",
  "Access-Control-Allow-Headers": "authentication-token",
  "Access-Control-Expose-Headers": "authentication-token",
});

function ResightingAdminRecord() {
  const BASE_URL_LOC = process.env.REACT_APP_SERVER_LOCATION;
  const appStates = useContext(AppContext);
  const navigate = useNavigate();
  const { recordId: recordIdFromUrl } = useParams();
  const [selectedBlockedRecordId, setSelectedBlockedRecordId] = useState(null);
  const [selectedBlockedRow, setSelectedBlockedRow] = useState(null);
  const [selectedRecordDetails, setSelectedRecordDetails] = useState(null);
  const [isLoadingRecordDetails, setIsLoadingRecordDetails] = useState(false);
  const [recordDetailsError, setRecordDetailsError] = useState("");
  const [resolutionNote, setResolutionNote] = useState("");
  const [isResolving, setIsResolving] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastSeverity, setToastSeverity] = useState("success");
  const [assignmentLogs, setAssignmentLogs] = useState([]);
  const [isLoadingLogs, setIsLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState("");

  const showToast = (message, severity = "success") => {
    setToastMessage(message);
    setToastSeverity(severity);
    setToastOpen(true);
  };

  useEffect(() => {
    if (!recordIdFromUrl) {
      return;
    }

    const parsedRecordId = Number(recordIdFromUrl);
    if (!Number.isInteger(parsedRecordId) || parsedRecordId <= 0) {
      setSelectedBlockedRecordId(null);
      setSelectedBlockedRow(null);
      setRecordDetailsError("Invalid record id.");
      return;
    }

    setSelectedBlockedRecordId(parsedRecordId);
    setSelectedBlockedRow({
      resighting_id: parsedRecordId,
      qc_status: null,
      status: null,
      qc_email: null,
      qc_remarks: null,
      access: null,
    });
    setSelectedRecordDetails(null);
    setRecordDetailsError("");
    setResolutionNote("");
    setIsLoadingRecordDetails(true);

    let cancelled = false;

    (async () => {
      try {
        const authToken = appStates.getToken();
        if (!authToken) {
          if (!cancelled) {
            setRecordDetailsError("Session expired. Please log in again.");
          }
          return;
        }

        const response = await fetch(`${BASE_URL_LOC}/api/resighting/${parsedRecordId}`, {
          method: "GET",
          redirect: "follow",
          headers: buildAuthHeaders(authToken),
          mode: "cors",
        });

        const body = await response.json();
        if (cancelled) {
          return;
        }
        if (!response.ok) {
          setRecordDetailsError(body?.error || "Unable to load this record right now.");
          return;
        }

        if (!body?.data) {
          setRecordDetailsError("No details found for this record.");
          return;
        }

        const qa = body.data?.qc_assignment;
        setSelectedBlockedRow({
          resighting_id: parsedRecordId,
          qc_status: qa?.qc_status ?? null,
          status: qa?.status ?? null,
          qc_email: qa?.qc_email ?? null,
          qc_remarks: qa?.qc_remarks ?? null,
          admin_remarks: qa?.admin_remarks ?? null,
          access: qa?.access ?? null,
        });

        setSelectedRecordDetails(
          buildRecordDetailsFromApi(body.data, { usesPagesLinks: true })
        );
      } catch (error) {
        if (!cancelled) {
          setRecordDetailsError("Unable to load this record right now.");
        }
      } finally {
        if (!cancelled) {
          setIsLoadingRecordDetails(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [recordIdFromUrl, BASE_URL_LOC, appStates]);

  useEffect(() => {
    if (!selectedBlockedRecordId) {
      return;
    }

    let cancelled = false;
    setAssignmentLogs([]);
    setLogsError("");
    setIsLoadingLogs(true);

    (async () => {
      try {
        const authToken = appStates.getToken();
        if (!authToken) {
          if (!cancelled) {
            setLogsError("Session expired. Please log in again.");
          }
          return;
        }

        const response = await fetch(
          `${BASE_URL_LOC}/api/resighting/${selectedBlockedRecordId}/logs`,
          {
            method: "GET",
            redirect: "follow",
            headers: buildAuthHeaders(authToken),
            mode: "cors",
          }
        );

        const body = await response.json();
        if (cancelled) {
          return;
        }

        if (!response.ok) {
          setLogsError(body?.error || "Unable to load activity for this record.");
          return;
        }

        setAssignmentLogs(Array.isArray(body?.data) ? body.data : []);
      } catch (error) {
        if (!cancelled) {
          setLogsError("Unable to load activity for this record.");
        }
      } finally {
        if (!cancelled) {
          setIsLoadingLogs(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedBlockedRecordId, BASE_URL_LOC, appStates]);

  const handleResolveClose = () => {
    setSelectedBlockedRecordId(null);
    setSelectedBlockedRow(null);
    setSelectedRecordDetails(null);
    setRecordDetailsError("");
    setIsLoadingRecordDetails(false);
    setResolutionNote("");
    setAssignmentLogs([]);
    setLogsError("");
    setIsLoadingLogs(false);
    navigate("/home/resighting/admin");
  };

  const submitResolveAction = async (action) => {
    if (!selectedBlockedRecordId) {
      return;
    }

    const trimmedNote = resolutionNote.trim();
    if (action === "requeue" && !trimmedNote) {
      showToast("Resolution note is required.", "warning");
      return;
    }

    const authToken = appStates.getToken();
    if (!authToken) {
      showToast("Session expired. Please log in again.", "error");
      return;
    }

    setIsResolving(true);
    try {
      const payload = { action };
      if (trimmedNote) {
        payload.resolution_note = trimmedNote;
      }

      const response = await fetch(
        `${BASE_URL_LOC}/api/resighting/admin/submissions/${selectedBlockedRecordId}/resolve`,
        {
          method: "PUT",
          redirect: "follow",
          headers: {
            "Content-Type": "application/json",
            ...buildAuthHeaders(authToken),
          },
          mode: "cors",
          body: JSON.stringify(payload),
        }
      );

      const body = await response.json();
      const backendMessage =
        body?.message ||
        body?.data?.message ||
        (typeof body?.data === "string" ? body.data : "") ||
        body?.error;

      if (!response.ok) {
        showToast(backendMessage || "Unable to update record right now.", "error");
        return;
      }

      showToast(
        backendMessage ||
          (action === "hold"
            ? "Record placed on hold."
            : "Record resolved and sent back to queue."),
        "success"
      );
      handleResolveClose();
    } catch (error) {
      showToast("Unable to update record right now.", "error");
    } finally {
      setIsResolving(false);
    }
  };

  const handleResolveSave = () => submitResolveAction("requeue");
  const handleHoldRecord = () => submitResolveAction("hold");

  const record = selectedRecordDetails;
  const qcStatusRaw = selectedBlockedRow?.qc_status;
  const assignmentStatus = selectedBlockedRow?.status;
  const qcStatusLabel = formatQcStatus(qcStatusRaw);
  const qcAccessStatus = getQcAccessStatus(selectedBlockedRow?.access);
  const isBlockedForResolution = qcStatusRaw === "blocked";
  const isOnHoldForResolution = assignmentStatus === "on_hold";
  const canResolveRecord = isBlockedForResolution || isOnHoldForResolution;

  if (selectedBlockedRecordId !== null) {
    return (
    <div className="resighting-qc-page">
      <div className="resighting-qc-detail-header-wrap" style={{ marginBottom: "0.7rem" }}>
        <div className="resighting-qc-detail-header">
          <h2 className="resighting-qc-section-title">
            {isOnHoldForResolution
              ? `Resolve on-hold record #${selectedBlockedRecordId}`
              : isBlockedForResolution
                ? `Resolve blocked record #${selectedBlockedRecordId}`
                : `Record #${selectedBlockedRecordId}`}
          </h2>
          <button type="button" className="resighting-qc-btn" onClick={handleResolveClose} disabled={isResolving}>
            Back
          </button>
        </div>
        {!isLoadingRecordDetails ? (
          <>
            <p className="resighting-admin-paragraph">{qcStatusLabel}</p>
            <p className="resighting-admin-paragraph">
              Intern&apos;s email: {selectedBlockedRow?.qc_email || "-"}
              {qcAccessStatus ? (
                <span
                  className={`resighting-qc-access-capsule resighting-qc-access-capsule--${
                    qcAccessStatus.isActive ? "active" : "inactive"
                  }`}
                >
                  {qcAccessStatus.label}
                </span>
              ) : null}
            </p>
          </>
        ) : null}
        <p className="resighting-qc-blocker-remarks-wrap">
          <span className="resighting-qc-remarks-label" style={{ display: "inline-block" }}>
            Intern&apos;s remarks:{" "}
          </span>
          {selectedBlockedRow?.qc_remarks || "-"}
        </p>

        {/* {
          selectedBlockedRow?.admin_remarks && (
            <p className="resighting-qc-blocker-remarks-wrap">
              <span className="resighting-qc-remarks-label" style={{ display: "inline-block" }}>
                Admin&apos;s remarks:{" "}
              </span>
              {selectedBlockedRow?.admin_remarks || "-"}
            </p>
          )
        } */}
        
      </div>

      <ResightingActivitySection
        logs={assignmentLogs}
        isLoading={isLoadingLogs}
        errorMessage={logsError}
      />

      {isLoadingRecordDetails ? (
        <Card className="resighting-qc-card">
          <CardContent>
            <p className="resighting-qc-muted">Loading record details...</p>
          </CardContent>
        </Card>
      ) : recordDetailsError ? (
        <Card className="resighting-qc-card">
          <CardContent>
            <p className="resighting-qc-muted">{recordDetailsError}</p>
          </CardContent>
        </Card>
      ) : (
        <ResightingRecordDetails record={record} />
      )}

      {canResolveRecord ? (
        <div className="resighting-qc-card">
          <h3 className="resighting-admin-heading">Resolution note</h3>
          <p className="resighting-admin-paragraph" style={{ marginTop: "0.3rem" }}>
            {isOnHoldForResolution
              ? "Required when sending back to queue."
              : "Required when sending back to queue. Optional when placing on hold."}
          </p>
          <textarea
            id="admin-resolution-note"
            className="resighting-qc-remarks-input"
            style={{ marginTop: "0.75rem", fontFamily: "inherit" }}
            rows={5}
            value={resolutionNote}
            onChange={(e) => setResolutionNote(e.target.value)}
            placeholder="e.g. Coordinates were ambiguous, corrected to decimal degrees. Proceed."
          />
          <div style={{ display: "flex", gap: "0.6rem", marginTop: "0.75rem" }}>
            <button
              type="button"
              className="resighting-qc-btn"
              onClick={handleResolveSave}
              disabled={isLoadingRecordDetails || !!recordDetailsError || isResolving}
            >
              {isResolving ? "Sending..." : "Send back to queue"}
            </button>
            {!isOnHoldForResolution && (
              <button
                type="button"
                className="resighting-hold-btn"
                onClick={handleHoldRecord}
                disabled={isLoadingRecordDetails || !!recordDetailsError || isResolving}
              >
                {isResolving ? "Updating..." : "Hold the record"}
              </button>
            )}
          </div>
        </div>
      ) : null}

      <Snackbar
        open={toastOpen}
        autoHideDuration={6000}
        onClose={() => setToastOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setToastOpen(false)}
          severity={toastSeverity}
          sx={{ width: "100%" }}
        >
          {toastMessage}
        </Alert>
      </Snackbar>
    </div>
    );
  }

  if (recordDetailsError) {
    return (
      <div className="resighting-qc-page">
        <div className="resighting-qc-detail-header-wrap" style={{ marginBottom: "0.7rem" }}>
          <div className="resighting-qc-detail-header">
            <h2 className="resighting-qc-section-title">Resolve blocked record</h2>
            <button type="button" className="resighting-qc-btn" onClick={handleResolveClose}>
              Back
            </button>
          </div>
        </div>
        <Card className="resighting-qc-card">
          <CardContent>
            <p className="resighting-qc-muted">{recordDetailsError}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="resighting-qc-page">
      <Card className="resighting-qc-card">
        <CardContent>
          <p className="resighting-qc-muted">Loading record...</p>
        </CardContent>
      </Card>
    </div>
  );
}

export default ResightingAdminRecord;
